import { AdminLayout } from "./AdminLayout";
import { ArrowLeft, Mail, Phone, Building2, Calendar, Clock, Plus, MessageSquare, PhoneCall, Video, CheckSquare, FileText, Trash2, Edit2, Pin, Bell } from "lucide-react";
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const LeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  
  // Form states
  const [activityForm, setActivityForm] = useState({
    activity_type: 'call', subject: '', description: '', outcome: '', duration_minutes: ''
  });
  const [noteForm, setNoteForm] = useState({ content: '', is_pinned: false });
  const [reminderForm, setReminderForm] = useState({ title: '', description: '', due_at: '' });

  const fetchLead = async () => {
    try {
      const response = await axios.get(`${API}/crm/leads/${leadId}`, { withCredentials: true });
      setLead(response.data);
    } catch (error) {
      toast.error("Failed to fetch lead");
      navigate('/admin/leads');
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/crm/activities`, { 
        params: { lead_id: leadId }, 
        withCredentials: true 
      });
      setActivities(response.data);
    } catch (error) {
      console.error("Failed to fetch activities", error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/crm/notes`, { 
        params: { lead_id: leadId }, 
        withCredentials: true 
      });
      setNotes(response.data);
    } catch (error) {
      console.error("Failed to fetch notes", error);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API}/crm/reminders`, { 
        params: { lead_id: leadId }, 
        withCredentials: true 
      });
      setReminders(response.data);
    } catch (error) {
      console.error("Failed to fetch reminders", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchLead();
      await Promise.all([fetchActivities(), fetchNotes(), fetchReminders()]);
      setLoading(false);
    };
    loadData();
  }, [leadId]);

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/crm/activities`, {
        ...activityForm,
        lead_id: leadId,
        duration_minutes: activityForm.duration_minutes ? parseInt(activityForm.duration_minutes) : null
      }, { withCredentials: true });
      toast.success("Activity logged");
      setActivityDialogOpen(false);
      setActivityForm({ activity_type: 'call', subject: '', description: '', outcome: '', duration_minutes: '' });
      fetchActivities();
    } catch (error) {
      toast.error("Failed to log activity");
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/crm/notes`, {
        ...noteForm,
        lead_id: leadId
      }, { withCredentials: true });
      toast.success("Note added");
      setNoteDialogOpen(false);
      setNoteForm({ content: '', is_pinned: false });
      fetchNotes();
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const handleCreateReminder = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/crm/reminders`, {
        ...reminderForm,
        lead_id: leadId
      }, { withCredentials: true });
      toast.success("Reminder created");
      setReminderDialogOpen(false);
      setReminderForm({ title: '', description: '', due_at: '' });
      fetchReminders();
    } catch (error) {
      toast.error("Failed to create reminder");
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await axios.delete(`${API}/crm/activities/${activityId}`, { withCredentials: true });
      toast.success("Activity deleted");
      fetchActivities();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/crm/notes/${noteId}`, { withCredentials: true });
      toast.success("Note deleted");
      fetchNotes();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const completeReminder = async (reminderId) => {
    try {
      await axios.put(`${API}/crm/reminders/${reminderId}`, { is_completed: true }, { withCredentials: true });
      toast.success("Reminder completed");
      fetchReminders();
    } catch (error) {
      toast.error("Failed to update reminder");
    }
  };

  const statusColors = {
    new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    contacted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    qualified: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    proposal: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    won: 'bg-green-500/20 text-green-400 border-green-500/30',
    lost: 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  const activityIcons = {
    call: <PhoneCall className="w-4 h-4" />,
    email: <Mail className="w-4 h-4" />,
    meeting: <Video className="w-4 h-4" />,
    note: <FileText className="w-4 h-4" />,
    task: <CheckSquare className="w-4 h-4" />
  };

  if (loading) {
    return (
      <AdminLayout title="Lead Details">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-[#2E2E2E] rounded" />
          <div className="h-32 bg-[#2E2E2E] rounded" />
        </div>
      </AdminLayout>
    );
  }

  if (!lead) return null;

  return (
    <AdminLayout title="Lead">
      <div className="space-y-6" data-testid="lead-detail">
        {/* Back button */}
        <Link to="/admin/leads" className="inline-flex items-center gap-2 text-sm text-[#8A8A8A] hover:text-[#F5F4EF] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Leads
        </Link>

        {/* Lead Header */}
        <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold">{lead.name}</h1>
                <span className={`px-3 py-1 text-xs uppercase tracking-wider border ${statusColors[lead.status]}`}>
                  {lead.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 text-[#8A8A8A]">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${lead.email}`} className="hover:text-[#5F8FB8]">{lead.email}</a>
                </div>
                {lead.phone && (
                  <div className="flex items-center gap-2 text-[#8A8A8A]">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${lead.phone}`} className="hover:text-[#5F8FB8]">{lead.phone}</a>
                  </div>
                )}
                {lead.company && (
                  <div className="flex items-center gap-2 text-[#8A8A8A]">
                    <Building2 className="w-4 h-4" />
                    {lead.company}
                  </div>
                )}
              </div>
              <div className="flex gap-4 text-xs text-[#8A8A8A]">
                <span>Source: <span className="text-[#F5F4EF] capitalize">{lead.source?.replace('_', ' ')}</span></span>
                <span>Created: <span className="text-[#F5F4EF]">{new Date(lead.created_at).toLocaleDateString('en-GB')}</span></span>
              </div>
              {lead.message && (
                <div className="mt-4 p-3 bg-[#0E0E0E] border border-[#2E2E2E] text-sm text-[#8A8A8A]">
                  <div className="text-xs uppercase tracking-wider text-[#5F8FB8] mb-1">Message</div>
                  {lead.message}
                </div>
              )}
            </div>
            
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2">
              <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
                <DialogTrigger asChild>
                  <button className="btn-primary text-sm flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Log Activity
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                  <DialogHeader>
                    <DialogTitle>Log Activity</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateActivity} className="space-y-4 mt-4">
                    <select 
                      value={activityForm.activity_type}
                      onChange={(e) => setActivityForm({...activityForm, activity_type: e.target.value})}
                      className="input-field"
                    >
                      <option value="call">Phone Call</option>
                      <option value="email">Email</option>
                      <option value="meeting">Meeting</option>
                      <option value="note">Note</option>
                      <option value="task">Task</option>
                    </select>
                    <input 
                      type="text" 
                      placeholder="Subject *" 
                      required
                      value={activityForm.subject}
                      onChange={(e) => setActivityForm({...activityForm, subject: e.target.value})}
                      className="input-field"
                    />
                    <textarea 
                      placeholder="Description"
                      rows={3}
                      value={activityForm.description}
                      onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                      className="input-field resize-none"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <input 
                        type="text" 
                        placeholder="Outcome"
                        value={activityForm.outcome}
                        onChange={(e) => setActivityForm({...activityForm, outcome: e.target.value})}
                        className="input-field"
                      />
                      <input 
                        type="number" 
                        placeholder="Duration (mins)"
                        value={activityForm.duration_minutes}
                        onChange={(e) => setActivityForm({...activityForm, duration_minutes: e.target.value})}
                        className="input-field"
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <button type="button" onClick={() => setActivityDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                      <button type="submit" className="btn-primary text-sm">Log Activity</button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              
              <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
                <DialogTrigger asChild>
                  <button className="btn-secondary text-sm flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Add Note
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                  <DialogHeader>
                    <DialogTitle>Add Note</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateNote} className="space-y-4 mt-4">
                    <textarea 
                      placeholder="Write a note..."
                      rows={4}
                      required
                      value={noteForm.content}
                      onChange={(e) => setNoteForm({...noteForm, content: e.target.value})}
                      className="input-field resize-none"
                    />
                    <label className="flex items-center gap-2 text-sm">
                      <input 
                        type="checkbox"
                        checked={noteForm.is_pinned}
                        onChange={(e) => setNoteForm({...noteForm, is_pinned: e.target.checked})}
                        className="w-4 h-4"
                      />
                      Pin this note
                    </label>
                    <div className="flex justify-end gap-3">
                      <button type="button" onClick={() => setNoteDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                      <button type="submit" className="btn-primary text-sm">Add Note</button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
                <DialogTrigger asChild>
                  <button className="btn-secondary text-sm flex items-center gap-2">
                    <Bell className="w-4 h-4" /> Set Reminder
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                  <DialogHeader>
                    <DialogTitle>Set Reminder</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateReminder} className="space-y-4 mt-4">
                    <input 
                      type="text" 
                      placeholder="Reminder title *"
                      required
                      value={reminderForm.title}
                      onChange={(e) => setReminderForm({...reminderForm, title: e.target.value})}
                      className="input-field"
                    />
                    <input 
                      type="datetime-local"
                      required
                      value={reminderForm.due_at}
                      onChange={(e) => setReminderForm({...reminderForm, due_at: e.target.value})}
                      className="input-field"
                    />
                    <textarea 
                      placeholder="Description (optional)"
                      rows={2}
                      value={reminderForm.description}
                      onChange={(e) => setReminderForm({...reminderForm, description: e.target.value})}
                      className="input-field resize-none"
                    />
                    <div className="flex justify-end gap-3">
                      <button type="button" onClick={() => setReminderDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                      <button type="submit" className="btn-primary text-sm">Set Reminder</button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="activities" className="w-full">
          <TabsList className="bg-[#1C1C1C] border border-[#2E2E2E] p-1">
            <TabsTrigger value="activities" className="data-[state=active]:bg-[#5F8FB8] data-[state=active]:text-[#0E0E0E]">
              Activities ({activities.length})
            </TabsTrigger>
            <TabsTrigger value="notes" className="data-[state=active]:bg-[#5F8FB8] data-[state=active]:text-[#0E0E0E]">
              Notes ({notes.length})
            </TabsTrigger>
            <TabsTrigger value="reminders" className="data-[state=active]:bg-[#5F8FB8] data-[state=active]:text-[#0E0E0E]">
              Reminders ({reminders.filter(r => !r.is_completed).length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="activities" className="mt-4">
            <div className="space-y-3">
              {activities.length === 0 ? (
                <div className="text-center py-8 text-[#8A8A8A] border border-[#2E2E2E]">
                  No activities logged yet
                </div>
              ) : (
                activities.map((activity) => (
                  <div key={activity.activity_id} className="bg-[#1C1C1C] border border-[#2E2E2E] p-4 group">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-[#5F8FB8]/10 text-[#5F8FB8] flex items-center justify-center">
                          {activityIcons[activity.activity_type]}
                        </div>
                        <div>
                          <div className="font-medium">{activity.subject}</div>
                          <div className="text-xs text-[#8A8A8A] mt-1 flex items-center gap-3">
                            <span className="capitalize">{activity.activity_type}</span>
                            {activity.duration_minutes && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {activity.duration_minutes} mins
                              </span>
                            )}
                            <span>{new Date(activity.created_at).toLocaleString('en-GB')}</span>
                          </div>
                          {activity.description && (
                            <div className="text-sm text-[#8A8A8A] mt-2">{activity.description}</div>
                          )}
                          {activity.outcome && (
                            <div className="text-sm mt-2">
                              <span className="text-[#5F8FB8]">Outcome:</span> {activity.outcome}
                            </div>
                          )}
                        </div>
                      </div>
                      <button 
                        onClick={() => handleDeleteActivity(activity.activity_id)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#2E2E2E] text-[#8A8A8A] hover:text-red-400 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="notes" className="mt-4">
            <div className="space-y-3">
              {notes.length === 0 ? (
                <div className="text-center py-8 text-[#8A8A8A] border border-[#2E2E2E]">
                  No notes yet
                </div>
              ) : (
                notes.map((note) => (
                  <div key={note.note_id} className={`bg-[#1C1C1C] border p-4 group ${note.is_pinned ? 'border-[#5F8FB8]/50' : 'border-[#2E2E2E]'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {note.is_pinned && (
                          <div className="flex items-center gap-1 text-xs text-[#5F8FB8] mb-2">
                            <Pin className="w-3 h-3" /> Pinned
                          </div>
                        )}
                        <div className="text-sm whitespace-pre-wrap">{note.content}</div>
                        <div className="text-xs text-[#8A8A8A] mt-2">
                          {new Date(note.created_at).toLocaleString('en-GB')}
                        </div>
                      </div>
                      <button 
                        onClick={() => handleDeleteNote(note.note_id)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#2E2E2E] text-[#8A8A8A] hover:text-red-400 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="reminders" className="mt-4">
            <div className="space-y-3">
              {reminders.length === 0 ? (
                <div className="text-center py-8 text-[#8A8A8A] border border-[#2E2E2E]">
                  No reminders set
                </div>
              ) : (
                reminders.map((reminder) => (
                  <div key={reminder.reminder_id} className={`bg-[#1C1C1C] border border-[#2E2E2E] p-4 ${reminder.is_completed ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => !reminder.is_completed && completeReminder(reminder.reminder_id)}
                          className={`w-5 h-5 border-2 flex items-center justify-center ${reminder.is_completed ? 'bg-green-500 border-green-500' : 'border-[#8A8A8A] hover:border-[#5F8FB8]'}`}
                        >
                          {reminder.is_completed && <CheckSquare className="w-3 h-3 text-white" />}
                        </button>
                        <div>
                          <div className={`font-medium ${reminder.is_completed ? 'line-through' : ''}`}>{reminder.title}</div>
                          <div className="text-xs text-[#8A8A8A] flex items-center gap-2 mt-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(reminder.due_at).toLocaleString('en-GB')}
                          </div>
                          {reminder.description && (
                            <div className="text-sm text-[#8A8A8A] mt-1">{reminder.description}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default LeadDetail;
